import { openAccordionTarget } from "./accordion.js";

const MAX_RESULTS = 8;
const MIN_QUERY_LENGTH = 3;
const DEBOUNCE_MS = 150;
const NO_RESULTS_LABEL = "Keine Ergebnisse";

const prefersReducedMotion = window.matchMedia(
  "(prefers-reduced-motion: reduce)",
).matches;

const MATCH_WORD_START = "word-start";
const MATCH_ANYWHERE = "anywhere";
const WORD_CHAR = /[\p{L}\p{N}]/u;

const normalizeText = (text) =>
  String(text).toLowerCase().trim().replace(/\s+/g, " ");

const getQueryTerms = (query) => {
  const normalized = normalizeText(query);

  if (normalized === "") {
    return [];
  }

  return [...new Set(normalized.split(" ").filter(Boolean))];
};

const escapeRegExp = (text) => text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const getTermPattern = (term) =>
  escapeRegExp(term).replace(/ss|ß/g, "(?:ss|ß)");

// Lookbehind is avoided for older Safari; the boundary is captured instead.
const createTermRegExp = (term, mode) =>
  new RegExp(
    `(${mode === MATCH_WORD_START ? "^|[^\\p{L}\\p{N}]" : ""})(${getTermPattern(term)})`,
    "giu",
  );

const findTermMatches = (text, term, mode) =>
  [...String(text).matchAll(createTermRegExp(term, mode))].map((match) => {
    const start = match.index + match[1].length;

    return { start, end: start + match[2].length };
  });

const mergeHighlightRanges = (ranges) => {
  if (ranges.length === 0) {
    return [];
  }

  const sorted = [...ranges].sort((a, b) => a.start - b.start);
  const merged = [sorted[0]];

  sorted.slice(1).forEach((range) => {
    const previous = merged[merged.length - 1];

    if (range.start <= previous.end) {
      previous.end = Math.max(previous.end, range.end);
      return;
    }

    merged.push(range);
  });

  return merged;
};

const getHighlightRanges = (text, terms, mode) =>
  mergeHighlightRanges(
    terms.flatMap((term) => findTermMatches(text, term, mode)),
  );

const HIGHLIGHT_CLASS = "accordion-item__search-mark";

const appendHighlightedFragment = (text, ranges) => {
  const fragment = document.createDocumentFragment();
  let cursor = 0;

  ranges.forEach(({ start, end }) => {
    if (cursor < start) {
      fragment.appendChild(document.createTextNode(text.slice(cursor, start)));
    }

    const mark = document.createElement("span");

    mark.className = HIGHLIGHT_CLASS;
    mark.textContent = text.slice(start, end);
    fragment.appendChild(mark);
    cursor = end;
  });

  if (cursor < text.length) {
    fragment.appendChild(document.createTextNode(text.slice(cursor)));
  }

  return fragment;
};

const highlightTextNode = (node, terms, mode) => {
  const text = node.textContent ?? "";
  const ranges = getHighlightRanges(text, terms, mode);

  if (ranges.length === 0) {
    return;
  }

  node.replaceWith(appendHighlightedFragment(text, ranges));
};

const highlightElementText = (root, terms, mode) => {
  if (!root || terms.length === 0) {
    return;
  }

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const textNodes = [];

  while (walker.nextNode()) {
    const node = walker.currentNode;

    if (node.textContent?.trim() === "") {
      continue;
    }

    if (node.parentElement?.closest(`.${HIGHLIGHT_CLASS}`)) {
      continue;
    }

    textNodes.push(node);
  }

  textNodes.forEach((node) => {
    highlightTextNode(node, terms, mode);
  });
};

const clearAccordionHighlights = (stack) => {
  stack.querySelectorAll(`.${HIGHLIGHT_CLASS}`).forEach((mark) => {
    mark.replaceWith(document.createTextNode(mark.textContent ?? ""));
  });

  stack
    .querySelectorAll(".accordion-item__title, .accordion-item__content")
    .forEach((element) => {
      element.normalize();
    });
};

const highlightAccordionItem = (item, terms, mode) => {
  if (terms.length === 0) {
    return;
  }

  highlightElementText(
    item.querySelector(".accordion-item__title"),
    terms,
    mode,
  );
  highlightElementText(
    item.querySelector(".accordion-item__content"),
    terms,
    mode,
  );
};

const buildIndex = (stack) =>
  [...stack.querySelectorAll(".accordion-item")]
    .map((item) => {
      const panel = item.querySelector(".accordion-item__panel");
      const group = item.closest(".accordion-group");
      const titleEl = item.querySelector(".accordion-item__title");

      return {
        itemId: panel?.id ?? "",
        groupId: group?.id ?? "",
        title: titleEl?.textContent?.trim() ?? "",
        searchText: item.dataset.searchText ?? "",
      };
    })
    .filter((entry) => entry.itemId !== "" && entry.title !== "");

// 3 = whole word, 2 = word start, 1 = inside a word, 0 = no match.
const getMatchStrength = (text, term) => {
  const wordStartMatches = findTermMatches(text, term, MATCH_WORD_START);

  if (wordStartMatches.length > 0) {
    const isWholeWord = wordStartMatches.some(
      ({ end }) => !WORD_CHAR.test(text.charAt(end)),
    );

    return {
      strength: isWholeWord ? 3 : 2,
      count: wordStartMatches.length,
    };
  }

  const anywhereMatches = findTermMatches(text, term, MATCH_ANYWHERE);

  return {
    strength: anywhereMatches.length > 0 ? 1 : 0,
    count: anywhereMatches.length,
  };
};

const scoreItem = (item, terms) => {
  let score = 0;

  terms.forEach((term) => {
    const title = getMatchStrength(item.title, term);
    const text = getMatchStrength(item.searchText, term);

    score +=
      title.strength * 4 + text.strength + Math.min(text.count, 3) * 0.25;
  });

  if (terms.length > 1) {
    const phrase = new RegExp(terms.map(getTermPattern).join("\\s+"), "iu");

    if (phrase.test(item.title)) {
      score += 6;
    } else if (phrase.test(item.searchText)) {
      score += 3;
    }
  }

  if (findTermMatches(item.title, terms[0], MATCH_WORD_START)[0]?.start === 0) {
    score += 1;
  }

  return score;
};

const matchesAllTerms = (item, terms, mode) =>
  terms.every(
    (term) => findTermMatches(item.searchText, term, mode).length > 0,
  );

const searchIndex = (index, query) => {
  const terms = getQueryTerms(query);

  if (terms.length === 0) {
    return { mode: MATCH_WORD_START, results: [] };
  }

  let mode = MATCH_WORD_START;
  let matches = index.filter((item) => matchesAllTerms(item, terms, mode));

  if (matches.length === 0) {
    mode = MATCH_ANYWHERE;
    matches = index.filter((item) => matchesAllTerms(item, terms, mode));
  }

  const results = matches
    .map((item) => ({ item, score: scoreItem(item, terms) }))
    .sort((a, b) => b.score - a.score)
    .slice(0, MAX_RESULTS)
    .map(({ item }) => item);

  return { mode, results };
};

const getResultOptions = (resultsEl) => [
  ...resultsEl.querySelectorAll(".faq-search__result"),
];

const scrollOptionIntoView = (resultsEl, option) => {
  if (!option) {
    return;
  }

  const resultsRect = resultsEl.getBoundingClientRect();
  const optionRect = option.getBoundingClientRect();

  if (optionRect.top < resultsRect.top) {
    resultsEl.scrollTop -= resultsRect.top - optionRect.top;
    return;
  }

  if (optionRect.bottom > resultsRect.bottom) {
    resultsEl.scrollTop += optionRect.bottom - resultsRect.bottom;
  }
};

const setActiveOption = (input, resultsEl, option) => {
  getResultOptions(resultsEl).forEach((result) => {
    result.setAttribute("aria-selected", "false");
    result.classList.remove("is-active");
  });

  if (!option) {
    input.removeAttribute("aria-activedescendant");
    return;
  }

  option.setAttribute("aria-selected", "true");
  option.classList.add("is-active");
  input.setAttribute("aria-activedescendant", option.id);
  scrollOptionIntoView(resultsEl, option);
};

const getActiveOption = (input, resultsEl) => {
  const activeId = input.getAttribute("aria-activedescendant");

  if (!activeId) {
    return null;
  }

  return resultsEl.querySelector(`#${CSS.escape(activeId)}`);
};

const moveActiveOption = (input, resultsEl, direction) => {
  const options = getResultOptions(resultsEl);

  if (options.length === 0) {
    return null;
  }

  const current = getActiveOption(input, resultsEl);
  let nextIndex = direction > 0 ? 0 : options.length - 1;

  if (current) {
    const currentIndex = options.indexOf(current);

    nextIndex = currentIndex + direction;

    if (nextIndex < 0) {
      nextIndex = 0;
    }

    if (nextIndex >= options.length) {
      nextIndex = options.length - 1;
    }
  }

  const next = options[nextIndex];

  setActiveOption(input, resultsEl, next);

  return next;
};

const setDropdownOpen = (input, resultsEl, isOpen) => {
  input.setAttribute("aria-expanded", String(isOpen));

  if (isOpen) {
    resultsEl.removeAttribute("hidden");
    return;
  }

  resultsEl.setAttribute("hidden", "");
  setActiveOption(input, resultsEl, null);
};

const setClearVisible = (clearButton, isVisible) => {
  if (isVisible) {
    clearButton.removeAttribute("hidden");
    return;
  }

  clearButton.setAttribute("hidden", "");
};

const clearSearch = (stack, input, clearButton, resultsEl) => {
  clearAccordionHighlights(stack);
  input.value = "";
  resultsEl.replaceChildren();
  setDropdownOpen(input, resultsEl, false);
  setClearVisible(clearButton, false);
  input.focus();
};

const scrollToAccordionItem = (item) => {
  item.scrollIntoView({
    behavior: prefersReducedMotion ? "auto" : "smooth",
    block: "start",
  });
};

const selectSearchResult = async (
  stack,
  input,
  resultsEl,
  resultButton,
  matchMode,
) => {
  const groupId = resultButton.dataset.faqGroupId ?? "";
  const itemId = resultButton.dataset.faqId ?? "";

  if (groupId === "" || itemId === "") {
    return;
  }

  const opened = await openAccordionTarget(stack, { groupId, itemId });

  if (!opened) {
    return;
  }

  setDropdownOpen(input, resultsEl, false);
  scrollToAccordionItem(opened.item);

  clearAccordionHighlights(stack);
  highlightAccordionItem(opened.item, getQueryTerms(input.value), matchMode);

  const trigger = opened.item.querySelector(".accordion-item__trigger");

  trigger?.focus({ preventScroll: true });
};

const renderResults = (resultsEl, results, resultsId) => {
  resultsEl.replaceChildren();

  if (results.length === 0) {
    const empty = document.createElement("p");

    empty.className = "faq-search__empty";
    empty.textContent = NO_RESULTS_LABEL;
    resultsEl.appendChild(empty);
    return;
  }

  results.forEach((result, index) => {
    const button = document.createElement("button");

    button.type = "button";
    button.className = "faq-search__result";
    button.id = `${resultsId}-option-${index + 1}`;
    button.dataset.faqId = result.itemId;
    button.dataset.faqGroupId = result.groupId;
    button.setAttribute("role", "option");
    button.setAttribute("aria-selected", "false");
    button.tabIndex = -1;
    button.textContent = result.title;
    resultsEl.appendChild(button);
  });
};

const initFaqSearch = (section) => {
  const stack = section.querySelector(".faq-list__stack");

  if (!stack) {
    return;
  }

  const search = section.querySelector(".faq-search");

  if (!search) {
    return;
  }

  const form = search.querySelector(".faq-search__form");
  const input = search.querySelector(".faq-search__input");
  const clearButton = search.querySelector(".faq-search__clear");
  const resultsEl = search.querySelector(".faq-search__results");

  if (!form || !input || !clearButton || !resultsEl) {
    return;
  }

  const index = buildIndex(stack);
  let debounceTimer = null;
  let matchMode = MATCH_WORD_START;

  const updateResults = () => {
    const query = normalizeText(input.value);

    setClearVisible(clearButton, query.length > 0);

    if (query.length < MIN_QUERY_LENGTH) {
      clearAccordionHighlights(stack);
      resultsEl.replaceChildren();
      setDropdownOpen(input, resultsEl, false);
      return;
    }

    const { mode, results } = searchIndex(index, input.value);

    matchMode = mode;
    renderResults(resultsEl, results, resultsEl.id);
    setActiveOption(input, resultsEl, null);
    setDropdownOpen(input, resultsEl, true);
  };

  input.addEventListener("input", () => {
    clearAccordionHighlights(stack);
    window.clearTimeout(debounceTimer);
    debounceTimer = window.setTimeout(updateResults, DEBOUNCE_MS);
  });

  input.addEventListener("focus", () => {
    updateResults();
  });

  clearButton.addEventListener("click", () => {
    clearSearch(stack, input, clearButton, resultsEl);
  });

  resultsEl.addEventListener("click", (event) => {
    const resultButton = event.target.closest(".faq-search__result");

    if (!resultButton) {
      return;
    }

    selectSearchResult(stack, input, resultsEl, resultButton, matchMode);
  });

  resultsEl.addEventListener("mousemove", (event) => {
    const resultButton = event.target.closest(".faq-search__result");

    if (!resultButton) {
      return;
    }

    setActiveOption(input, resultsEl, resultButton);
  });

  const selectHighlightedResult = () => {
    if (resultsEl.hasAttribute("hidden")) {
      return;
    }

    const options = getResultOptions(resultsEl);
    const target = getActiveOption(input, resultsEl) ?? options[0];

    if (target) {
      selectSearchResult(stack, input, resultsEl, target, matchMode);
    }
  };

  form.addEventListener("submit", (event) => {
    event.preventDefault();
    selectHighlightedResult();
  });

  input.addEventListener("keydown", (event) => {
    const isOpen = !resultsEl.hasAttribute("hidden");
    const options = getResultOptions(resultsEl);
    const hasValidQuery = normalizeText(input.value).length >= MIN_QUERY_LENGTH;

    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      if (!hasValidQuery) {
        return;
      }

      event.preventDefault();

      if (!isOpen) {
        updateResults();
      }

      if (getResultOptions(resultsEl).length === 0) {
        return;
      }

      moveActiveOption(input, resultsEl, event.key === "ArrowDown" ? 1 : -1);
      return;
    }

    if (event.key === "Home" && isOpen && options.length > 0) {
      event.preventDefault();
      setActiveOption(input, resultsEl, options[0]);
      return;
    }

    if (event.key === "End" && isOpen && options.length > 0) {
      event.preventDefault();
      setActiveOption(input, resultsEl, options[options.length - 1]);
      return;
    }

    if (event.key === "Enter") {
      if (!isOpen || options.length === 0) {
        return;
      }

      event.preventDefault();
      selectHighlightedResult();
      return;
    }

    if (event.key === "Escape") {
      if (input.value !== "") {
        clearSearch(stack, input, clearButton, resultsEl);
        return;
      }

      setDropdownOpen(input, resultsEl, false);
    }
  });

  document.addEventListener("click", (event) => {
    if (!search.contains(event.target)) {
      setDropdownOpen(input, resultsEl, false);
    }
  });

  setDropdownOpen(input, resultsEl, false);
};

document.querySelectorAll(".faq-list").forEach(initFaqSearch);
